import { Fragment } from "react";
import { Button, Flex, Form } from "antd";
import { Control, Controller, useFieldArray, UseFormSetValue, UseFormTrigger, FieldValues } from "react-hook-form";
import { ProjectParticipantInfo } from "@entities/project";
import { ParticipantsInfiniteScroll } from "@features/participants-infinite-scroll";
import { BasketIcon } from "@shared/assets/svg/icons";
import { useCoordinator, useResetState } from "@shared/lib/hooks";
import { useUnifiedAutoResize } from "@shared/utils";
import { ParticipantsTable } from "./participants-table";

interface OrganizationFieldsProps<T extends FieldValues> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  trigger: UseFormTrigger<T>;
}

export function OrganizationFields<T extends FieldValues>({
  control,
  setValue,
  trigger,
}: OrganizationFieldsProps<T>) {
  const { projectId } = useCoordinator();

  const { key, reset } = useResetState();

  // Автоматический resize iframe при любых изменениях DOM внутри блока организаций
  useUnifiedAutoResize();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "journalKeepersList" as any,
  });

  const handlePartySelect = (
    index: number,
    company: ProjectParticipantInfo,
  ) => {
    if (setValue) {
      (setValue as any)(`journalKeepersList.${index}.company`, {
        companyName: company.company.shortName,
        companyId: company.company.companyId,
        projectParticipantId: company.projectParticipantId,
      });
      (setValue as any)(`journalKeepersList.${index}.authorizedRepresentativesList`, []);
      (trigger as any)(`journalKeepersList.${index}.company`);
      (trigger as any)(`journalKeepersList.${index}.authorizedRepresentativesList`);
      reset();
    }
  };

  const handlePartyClear = (index: number) => {
    if (setValue) {
      (setValue as any)(`journalKeepersList.${index}.company`, {
        companyId: null,
        projectParticipantId: null,
      });
      (setValue as any)(`journalKeepersList.${index}.authorizedRepresentativesList`, []);
      reset();
    }
  };

  return (
    <Flex vertical gap="24px">
      {fields.map((field, index: number, array) => (
        <Fragment key={field.id}>
          <Flex gap="8px">
            <Controller
              control={control}
              name={`journalKeepersList.${index}.company` as any}
              render={({ fieldState }) => {
                const errorMessage = 
                  fieldState.error?.message ||
                  (fieldState.error && "Обязательное поле");
                
                return (
                  <Form.Item
                    validateStatus={errorMessage ? "error" : ""}
                    help={errorMessage}
                    label="Организация, осуществляющая строительство или организация, осуществляющая подготовку проектной документации, ведущая журнал"
                    style={{ margin: 0, flex: 1 }}
                  >
                    <ParticipantsInfiniteScroll
                      style={{
                        flex: "1 0",
                      }}
                        defaultValue={(field as any).company?.companyName || ""}
                      projectId={projectId}
                      onPartySelect={(company) =>
                        handlePartySelect(index, company)
                      }
                      onPartyClear={() => handlePartyClear(index)}
                    />
                  </Form.Item>
                );
              }}
            />
            {array.length > 1 && (
              <Button
                type="text"
                icon={<BasketIcon />}
                onClick={() => remove(index)}
              />
            )}
          </Flex>
          <ParticipantsTable key={key} control={control} index={index} trigger={trigger} />
        </Fragment>
      ))}
      <Button
        style={{ width: "fit-content" }}
        type="default"
        onClick={() =>
          append({
            company: {
              companyId: null as any,
              projectParticipantId: null as any,
            },
            authorizedRepresentativesList: [],
          } as any)
        }
      >
        Добавить организацию
      </Button>
    </Flex>
  );
}
